import React, { useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, TextField } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import BrandMark from './brand/BrandMark';

const COPY = {
  fr: {
    eyebrow: 'Sécurité du compte',
    verifyingTitle: 'Validation en cours…',
    verifyingBody: 'Nous confirmons votre adresse courriel de façon sécurisée.',
    verifiedTitle: 'Votre adresse courriel est confirmée',
    verifiedBody: 'Votre compte CourtClic est maintenant vérifié. Vous pouvez retourner à l’application.',
    recoveredTitle: 'Votre adresse courriel a été restaurée',
    recoveredBody: 'Le changement d’adresse a été annulé. Vous pouvez maintenant retourner à CourtClic.',
    resetTitle: 'Choisissez un nouveau mot de passe',
    resetBody: 'Créez un nouveau mot de passe pour votre compte CourtClic.',
    resetSuccessTitle: 'Votre mot de passe a été modifié',
    resetSuccessBody: 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.',
    password: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    savePassword: 'Modifier le mot de passe',
    mismatch: 'Les mots de passe ne correspondent pas.',
    shortPassword: 'Le mot de passe doit contenir au moins 6 caractères.',
    errorTitle: 'Ce lien ne fonctionne plus',
    expired: 'Ce lien a expiré. Retournez dans CourtClic et demandez un nouveau courriel.',
    invalid: 'Ce lien est invalide ou a déjà été utilisé. Demandez un nouveau courriel dans CourtClic.',
    network: 'Impossible de communiquer avec CourtClic. Vérifiez votre connexion et réessayez.',
    generic: 'Nous n’avons pas pu terminer cette action. Demandez un nouveau courriel dans CourtClic.',
    continue: 'Retourner à CourtClic',
    retry: 'Réessayer',
    language: 'English',
  },
  en: {
    eyebrow: 'Account security',
    verifyingTitle: 'Verifying your email…',
    verifyingBody: 'We are securely confirming your email address.',
    verifiedTitle: 'Your email address is confirmed',
    verifiedBody: 'Your CourtClic account is now verified. You can return to the app.',
    recoveredTitle: 'Your email address was restored',
    recoveredBody: 'The address change was cancelled. You can now return to CourtClic.',
    resetTitle: 'Choose a new password',
    resetBody: 'Create a new password for your CourtClic account.',
    resetSuccessTitle: 'Your password was changed',
    resetSuccessBody: 'You can now sign in with your new password.',
    password: 'New password',
    confirmPassword: 'Confirm password',
    savePassword: 'Change password',
    mismatch: 'The passwords do not match.',
    shortPassword: 'Your password must be at least 6 characters.',
    errorTitle: 'This link no longer works',
    expired: 'This link has expired. Return to CourtClic and request a new email.',
    invalid: 'This link is invalid or has already been used. Request a new email in CourtClic.',
    network: 'CourtClic could not be reached. Check your connection and try again.',
    generic: 'We could not complete this action. Request a new email in CourtClic.',
    continue: 'Return to CourtClic',
    retry: 'Try again',
    language: 'Français',
  },
};

const TRUSTED_CONTINUE_ORIGINS = new Set([
  'https://app.courtclic.com',
  'https://courtclic-new.web.app',
  'https://courtclicstaging-new.web.app',
  'https://stage.courtclic.com',
  'http://localhost:3000',
  'http://127.0.0.1:3000',
  'http://127.0.0.1:3001',
  'http://127.0.0.1:3002',
]);

export const getEmailActionLanguage = (search, fallbackIsFrench = true) => {
  const language = new URLSearchParams(search).get('lang')?.toLowerCase();
  if (language?.startsWith('fr')) return 'fr';
  if (language?.startsWith('en')) return 'en';
  return fallbackIsFrench ? 'fr' : 'en';
};

export const getSafeContinueUrl = (rawUrl, origin = window.location.origin) => {
  const fallback = `${origin}/login?verified=1`;
  if (!rawUrl) return fallback;

  try {
    const candidate = new URL(rawUrl);
    if (candidate.origin === origin || TRUSTED_CONTINUE_ORIGINS.has(candidate.origin)) {
      return candidate.toString();
    }
  } catch (error) {
    return fallback;
  }

  return fallback;
};

const getErrorMessage = (error, copy) => {
  if (error?.code === 'auth/expired-action-code') return copy.expired;
  if (error?.code === 'auth/invalid-action-code') return copy.invalid;
  if (error?.code === 'auth/network-request-failed') return copy.network;
  return copy.generic;
};

export default function EmailActionPage({ fallbackIsFrench = true }) {
  const search = window.location.search;
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const initialLanguage = getEmailActionLanguage(search, fallbackIsFrench);
  const actionCopy = COPY[initialLanguage];
  const [language, setLanguage] = useState(initialLanguage);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [retryable, setRetryable] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const copy = COPY[language];
  const mode = params.get('mode');
  const actionCode = params.get('oobCode');
  const continueUrl = getSafeContinueUrl(params.get('continueUrl'));

  useEffect(() => {
    let active = true;

    const completeAction = async () => {
      if (!actionCode || !['verifyEmail', 'resetPassword', 'recoverEmail'].includes(mode)) {
        if (active) {
          setMessage(actionCopy.invalid);
          setRetryable(false);
          setStatus('error');
        }
        return;
      }

      try {
        if (mode === 'resetPassword') {
          await firebase.auth().verifyPasswordResetCode(actionCode);
          if (active) setStatus('reset-ready');
          return;
        }

        await firebase.auth().applyActionCode(actionCode);

        if (mode === 'verifyEmail' && firebase.auth().currentUser) {
          await firebase.auth().currentUser.reload();
          await firebase.auth().currentUser.getIdToken(true);
        }

        if (active) setStatus(mode === 'verifyEmail' ? 'verified' : 'recovered');
      } catch (error) {
        if (active) {
          setMessage(getErrorMessage(error, actionCopy));
          setRetryable(error?.code === 'auth/network-request-failed');
          setStatus('error');
        }
      }
    };

    completeAction();
    return () => { active = false; };
  }, [actionCode, actionCopy, mode]);

  const handlePasswordReset = async (event) => {
    event.preventDefault();
    setMessage('');

    if (password.length < 6) {
      setMessage(copy.shortPassword);
      return;
    }
    if (password !== passwordConfirmation) {
      setMessage(copy.mismatch);
      return;
    }

    setPasswordBusy(true);
    try {
      await firebase.auth().confirmPasswordReset(actionCode, password);
      setStatus('reset-complete');
      setPassword('');
      setPasswordConfirmation('');
    } catch (error) {
      setMessage(getErrorMessage(error, copy));
      setRetryable(error?.code === 'auth/network-request-failed');
      setStatus('error');
    } finally {
      setPasswordBusy(false);
    }
  };

  const isLoading = status === 'loading';
  const isError = status === 'error';
  const isReset = status === 'reset-ready';
  const successCopy = status === 'recovered'
    ? [copy.recoveredTitle, copy.recoveredBody]
    : status === 'reset-complete'
      ? [copy.resetSuccessTitle, copy.resetSuccessBody]
      : [copy.verifiedTitle, copy.verifiedBody];

  return (
    <main className="email-action-page">
      <section className="email-action-card" aria-live="polite">
        <div className="email-action-brand">
          <BrandMark className="email-action-mark" />
          <span>CourtClic</span>
        </div>

        <button
          className="email-action-language"
          type="button"
          onClick={() => setLanguage((current) => current === 'fr' ? 'en' : 'fr')}
        >
          {copy.language}
        </button>

        <div className={`email-action-icon ${isError ? 'is-error' : ''}`} aria-hidden="true">
          {isLoading && <CircularProgress size={34} color="inherit" />}
          {isError && <ErrorOutlineRoundedIcon />}
          {isReset && <LockResetRoundedIcon />}
          {!isLoading && !isError && !isReset && <CheckCircleRoundedIcon />}
        </div>

        <p className="email-action-eyebrow">{copy.eyebrow}</p>

        {isLoading && (
          <>
            <h1>{copy.verifyingTitle}</h1>
            <p>{copy.verifyingBody}</p>
          </>
        )}

        {isError && (
          <>
            <h1>{copy.errorTitle}</h1>
            <p>{message}</p>
            {retryable ? (
              <Button variant="outlined" onClick={() => window.location.reload()}>{copy.retry}</Button>
            ) : (
              <Button variant="outlined" href={continueUrl}>{copy.continue}</Button>
            )}
          </>
        )}

        {isReset && (
          <>
            <h1>{copy.resetTitle}</h1>
            <p>{copy.resetBody}</p>
            <form className="email-action-form" onSubmit={handlePasswordReset}>
              <TextField
                fullWidth
                label={copy.password}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
              />
              <TextField
                fullWidth
                label={copy.confirmPassword}
                type="password"
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                autoComplete="new-password"
              />
              {message && <p className="email-action-form-error" role="alert">{message}</p>}
              <Button type="submit" variant="contained" disabled={passwordBusy}>
                {passwordBusy ? <CircularProgress size={20} color="inherit" /> : copy.savePassword}
              </Button>
            </form>
          </>
        )}

        {!isLoading && !isError && !isReset && (
          <>
            <h1>{successCopy[0]}</h1>
            <p>{successCopy[1]}</p>
            <Button variant="contained" href={continueUrl}>{copy.continue}</Button>
          </>
        )}
      </section>
    </main>
  );
}
